import { Link } from "react-router-dom";
import { PublicLayout } from "../components/layout/PublicLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { ScoreRing } from "../components/ui/ScoreRing";

const FEATURES = [
  {
    title: "Persona Card & Master Vault",
    desc: "Register your experiences and work style once, and get an auto-generated shareable global profile card.",
    icon: "🪪",
  },
  {
    title: "AI Adaptive Resume",
    desc: "We analyze the job posting and restructure your resume with the best-fit storyline from your master data.",
    icon: "📝",
  },
  {
    title: "Team-fit Matchmaker",
    desc: "Go beyond skill matching — we calculate both Career Fit and Team Culture Fit (Team-fit Score %) simultaneously.",
    icon: "🤝",
  },
  {
    title: "AI Chat Interview & Report",
    desc: "Conduct async chat interviews and receive personalized analysis reports — for both candidates and companies.",
    icon: "🎯",
  },
];

export default function Home() {
  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="mx-auto max-w-6xl px-4 pb-10 pt-10 sm:px-6 sm:pt-16 lg:px-8 lg:pt-20">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-16">
          {/* Left: Text */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold leading-tight text-navy sm:text-4xl lg:text-5xl lg:leading-[1.15]">
              Work Dynamic.
              <br />
              Fit Together.
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-slate-500 sm:text-base sm:leading-relaxed lg:max-w-lg">
              In the AI era, go beyond resumes. Find the perfect opportunity where your Work style and Team chemistry (We) are truly verified.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 lg:mt-8">
              <Link to="/login">
                <Button size="lg" icon={<GoogleG />} className="w-full sm:w-auto">
                  Continue with Google
                </Button>
              </Link>
              <p className="text-center text-[11px] text-slate-400 sm:text-left sm:text-xs">
                Get started in 1 second with your Google account.
              </p>
            </div>
          </div>

          {/* Right: Demo Persona Card */}
          <div className="flex-shrink-0 lg:w-[380px]">
            <Card className="border border-slate-100 shadow-card-hover">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 flex-shrink-0 rounded-full bg-gradient-to-br from-teal to-coral" />
                <div>
                  <p className="text-sm font-semibold text-navy sm:text-base">Global Persona Card</p>
                  <p className="text-xs text-slate-400 sm:text-sm">Product Designer · Seoul</p>
                </div>
                <div className="ml-auto">
                  <ScoreRing value={92} size={64} strokeWidth={6} label="Team-fit" />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {["UI/UX", "Figma", "Product", "Communication", "Agile"].map((tag) => (
                  <span key={tag} className="rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-500">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                {[
                  { label: "Career Fit", value: "94%" },
                  { label: "Team-fit", value: "92%" },
                  { label: "Experience", value: "7 yrs" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-xl bg-slate-50 px-2 py-2.5">
                    <p className="text-xs font-bold text-navy">{stat.value}</p>
                    <p className="text-[10px] text-slate-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card className="border border-slate-100 transition hover:shadow-card-hover">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-teal/10 text-lg">🙋‍♂️</div>
            <p className="text-sm font-semibold text-navy sm:text-base">Tired of rewriting your resume for every company?</p>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed sm:text-sm">
              Register once in your Master Vault, and your career profile auto-adapts to each company's needs.
            </p>
          </Card>
          <Card className="border border-slate-100 transition hover:shadow-card-hover">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-coral/10 text-lg">🏢</div>
            <p className="text-sm font-semibold text-navy sm:text-base">Hired for skills but lost to culture mismatch?</p>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed sm:text-sm">
              Our Team-fit Engine verifies candidates against your team's communication style and culture.
            </p>
          </Card>
        </div>
      </section>

      {/* 4 Core Features */}
      <section className="mx-auto max-w-6xl px-4 pb-14 sm:px-6 lg:px-8">
        <h2 className="text-xl font-bold text-navy sm:text-2xl">Core Features</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FEATURES.map((f, i) => (
            <Card key={f.title} className="flex gap-4 border border-slate-100 transition hover:shadow-card-hover">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-50 text-lg">
                {f.icon}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-teal/10 text-[10px] font-bold text-teal-dark">
                    {i + 1}
                  </span>
                  <p className="text-sm font-semibold text-navy sm:text-base">{f.title}</p>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500 sm:text-sm">{f.desc}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <Card className="bg-navy text-white sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-lg font-bold sm:text-xl">Build Your Global Career with personaw</p>
            <p className="mt-1 text-xs text-slate-300 sm:text-sm">AI-powered matching for better teams and careers.</p>
          </div>
          <Link to="/login" className="mt-4 inline-block sm:mt-0 sm:ml-6 sm:flex-shrink-0">
            <Button variant="secondary" size="md">
              Get Started →
            </Button>
          </Link>
        </Card>
      </section>
    </PublicLayout>
  );
}

function GoogleG() {
  return (
    <svg width={16} height={16} viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.61Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.19l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.9v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.95 10.69A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.16.28-1.69V4.98H.9A9 9 0 0 0 0 9c0 1.45.35 2.83.9 4.02l3.05-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .9 4.98l3.05 2.33C4.66 5.17 6.65 3.58 9 3.58Z" />
    </svg>
  );
}
